import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

const knownFeatureFlags = ['rtvWebCxOneChatBot', 'rtvWebMaintenanceMode', 'rtvWebInternetSales', 'rtvWebViaplayBrudd', 'rtvWebViaplayBruddPartial'] as const;
type BoolFlags = Record<(typeof knownFeatureFlags)[number], boolean>;
export type FeatureFlags = ReturnType<typeof useFeatureFLags>;

/**
 * Used for dev-purposes only to override the feature flags.
 * Normal usage would be to import "useFeatureFLags".
 * Run `localStorage.setItem('ld-panel', '')` to enable panel with checkboxes to toggle flags.
 */
export const useWritableFlagStore = create<Partial<BoolFlags>>()(
  persist(() => ({}), {
    name: 'rtv-flags',
    storage: createJSONStorage(() => sessionStorage),
  }),
);

/**
 * Typed feature flags from LaunchDarkly
 * See all flags at: https://app.launchdarkly.com/projects/riks-tv-web-apps-minside-bestill/flags
 */
export const useFeatureFLags = (): BoolFlags => {
  const ldFlags = useFlags<BoolFlags>();
  // init zustand store if no flags are set (only store the known keys)
  useEffect(() => {
    // streaming updates on localhost so update the store
    if (location.origin.includes('localhost')) {
      useWritableFlagStore.setState(filterOnKnownFlags(ldFlags));
    } else {
      useWritableFlagStore.setState((s) => {
        return Object.keys(s).length
          ? s
          : filterOnKnownFlags(ldFlags);
      });
    }
  }, [ldFlags]);

  const zustandFlags = useWritableFlagStore();
  // Until zustand store has values, return ldFlags
  return Object.keys(zustandFlags).length ? (zustandFlags as BoolFlags) : ldFlags;
};

const filterOnKnownFlags = (ldFlags: BoolFlags) => Object.fromEntries(Object.entries(ldFlags).filter(([key]) => knownFeatureFlags.includes(key as any)));


export const useViaplayBruddFeatures = (flags: Partial<BoolFlags>) => {
  return flags.rtvWebViaplayBrudd === true || flags.rtvWebViaplayBruddPartial === true;
}
